import { toast } from "../components/toast/toast";
import { isRelease150 } from "./release150";

// O-R67 Undo (release 1.5.0 polish; the SPA side of Domain\Rules\Polish\UndoRules.cs). Only reversible personal or
// low-risk actions offer Undo, in a toast that stays about 8 seconds. Signed and custody acts, approvals, publishes and
// prints never do: they keep their confirmations. The undo itself calls the original action's endpoints where it can.

export const UNDO_WINDOW_MS = 8000;

export const UNDOABLE_ACTIONS = [
  "unpin-tile",
  "delete-saved-filter",
  "mark-notice-read",
  "mark-all-notices-read",
  "snooze-notice",
  "dismiss"
] as const;

export type UndoableAction = (typeof UNDOABLE_ACTIONS)[number];

export const NEVER_UNDOABLE_WORDS = ["sign", "custody", "approve", "publish", "print"] as const;

export function isUndoable(action: string | null | undefined): action is UndoableAction {
  const a = (action ?? "").trim();
  if (!a) return false;
  if (NEVER_UNDOABLE_WORDS.some((w) => a.toLowerCase().includes(w))) return false;
  return (UNDOABLE_ACTIONS as readonly string[]).includes(a);
}

export interface UndoOffer {
  action: UndoableAction | string;
  /** What was just done, e.g. 'Deleted "My shop".' */
  done: string;
  /** What the person reads after the undo worked. */
  undone: string;
  undo: () => Promise<unknown>;
  /** Called after the undo succeeded (refresh the page's data). */
  onUndone?: () => void;
}

/**
 * Shows "done" with an Undo button for UNDO_WINDOW_MS. Returns the toast id, or null when nothing was offered (release
 * 1.5.0 off, or the action is not one that may be undone).
 */
export function offerUndo(offer: UndoOffer): number | null {
  if (!isRelease150() || !isUndoable(offer.action)) return null;
  return toast.success(offer.done, {
    durationMs: UNDO_WINDOW_MS,
    action: {
      label: "Undo",
      ariaLabel: `Undo: ${offer.done}`,
      onClick: () => {
        void offer
          .undo()
          .then(() => {
            offer.onUndone?.();
            toast.success(offer.undone);
          })
          .catch((error: unknown) => {
            toast.fromError("Could not undo that", error);
          });
      }
    }
  });
}
